import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  InputAdornment,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneEnabledIcon from "@mui/icons-material/PhoneEnabled";
import SendIcon from "@mui/icons-material/Send";
import { toast } from "react-toastify";
import { submit_general_enquiry } from "../../../api/ControllApi";

export default function GeneralEnquiry() {
  const [formData, setFormData] = useState({
    full_name: "",
    phone_number: "",
    enquiry: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone_number") {
      const cleanValue = value.replace(/\D/g, "");
      if (cleanValue.length > 10) return;
      setFormData((prev) => ({ ...prev, phone_number: cleanValue }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.full_name.trim()) newErrors.full_name = "Please enter your name";
    if (!formData.phone_number.trim()) {
      newErrors.phone_number = "Please enter phone number";
    } else if (formData.phone_number.trim().length !== 10) {
      newErrors.phone_number = "Please enter a valid 10-digit phone number";
    }
    if (!formData.enquiry.trim()) {
      newErrors.enquiry = "Please describe your requirement";
    } else if (formData.enquiry.trim().length < 11) {
      newErrors.enquiry = "Requirement must be at least 11 characters long";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      const res = await submit_general_enquiry(formData);
      if (res?.status === true || res?.success === true || res) {
        toast.success(res?.message || "General Enquiry submitted successfully");
        setFormData({
          full_name: "",
          phone_number: "",
          enquiry: "",
        });
      } else {
        toast.error(res?.message || "Failed to submit enquiry");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", mt: 4, mb: 4, px: 2 }}>
      <Paper
        elevation={3}
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          border: "1px solid #e0e0e0",
        }}
      >
        {/* Header Block matching visual */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #82A240 0%, #6A8A35 100%)",
            color: "#fff",
            p: 3,
            textAlign: "center",
          }}
        >
          <Typography variant="h5" fontWeight={700} gutterBottom>
            What You're Looking For?
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Tell Us Your Real Estate Needs
          </Typography>
        </Box>

        {/* Form Fields */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ p: 4, display: "flex", flexDirection: "column", gap: 3 }}
        >
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: -1 }}>
            | Customer Details
          </Typography>

          {/* Full Name */}
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              Full Name *
            </Typography>
            <TextField
              fullWidth
              name="full_name"
              placeholder="Enter your full name"
              value={formData.full_name}
              onChange={handleChange}
              error={!!errors.full_name}
              helperText={errors.full_name}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon sx={{ color: "#82A240" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                },
              }}
            />
          </Box>

          {/* Phone Number */}
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              Phone Number *
            </Typography>
            <TextField
              fullWidth
              name="phone_number"
              placeholder="Enter your phone number"
              value={formData.phone_number}
              onChange={handleChange}
              error={!!errors.phone_number}
              helperText={errors.phone_number}
              inputProps={{ maxLength: 10 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneEnabledIcon sx={{ color: "#82A240" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                },
              }}
            />
          </Box>

          {/* Requirement */}
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
              Requirement Message / Comments *
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              name="enquiry"
              placeholder="Describe what you're looking for..."
              value={formData.enquiry}
              onChange={handleChange}
              error={!!errors.enquiry}
              helperText={errors.enquiry}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                },
              }}
            />
          </Box>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            variant="contained"
            endIcon={<SendIcon />}
            sx={{
              background: "linear-gradient(135deg, #82A240 0%, #6A8A35 100%)",
              color: "#fff",
              textTransform: "none",
              borderRadius: "10px",
              py: 1.5,
              fontSize: 16,
              fontWeight: 600,
              boxShadow: "0 4px 14px rgba(130,162,64,0.4)",
              "&:hover": {
                background: "linear-gradient(135deg, #718F36 0%, #5E7A2E 100%)",
              },
            }}
          >
            {loading ? "Submitting..." : "Submit Request"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
